import {
  AppBar,
  Backdrop,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  Slide,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  TextField
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import GridOnIcon from '@mui/icons-material/GridOn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import PeopleIcon from '@mui/icons-material/People';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import StorageIcon from '@mui/icons-material/Storage';

import { Box, Snackbar, SnackbarContent } from '@mui/material';

import React, { useEffect, useState, useMemo } from 'react';
import { useGoogleFolderItems, useGoogleRootFolders } from '../../hooks/useGoogleDrive';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ROOT = { id: 'root', name: 'My Drive' };

const getIcon = (type, isShared) => {
  switch (type) {
    case 'folder':
      return isShared ? <FolderSharedIcon color="primary" /> : <FolderIcon color="primary" />;
    case 'document':
      return <ArticleIcon sx={{ color: '#1a8cff' }} />;
    case 'presentation':
      return <SlideshowIcon color="warning" />;
    case 'spreadsheet':
    case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return <GridOnIcon color="success" />;
    case 'pdf':
      return <PictureAsPdfIcon color="error" />;
    default:
      return <InsertDriveFileIcon />;
  }
};

/* ================= Breadcrumb ================= */

function DriveBreadcrumb({ breadcrumb, onNavigate, activeTab }) {
  return (
    <Breadcrumbs>
      {breadcrumb.map((item, index) => {
        const isLast = index === breadcrumb.length - 1;
        let displayName = item.name;
        if (index === 0) {
          if (activeTab === 'shared-with-me') {
            displayName = 'Shared with me';
          } else if (activeTab === 'my-drive') {
            displayName = 'My Drive';
          } else {
            displayName = 'All Files';
          }
        }

        return isLast ? (
          <Typography key={item.id} fontWeight={500}>
            {displayName}
          </Typography>
        ) : (
          <Link key={item.id} underline="hover" sx={{ cursor: 'pointer' }} onClick={() => onNavigate(index)}>
            {displayName}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

/* ================= Row ================= */

function DriveRow({ item, onOpen, onSelect }) {
  const isFolder = item.type === 'folder';
  const isShared = item.shared || item.sharedWithMe;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        cursor: isFolder ? 'pointer' : 'default',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
      onClick={() => (isFolder ? onOpen(item) : onSelect(item))}
    >
      {getIcon(item.type, isShared)}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14 }} title={item.name}>
          {item.name}
        </Typography>
        {isShared && (
          <Tooltip title={item.sharedWithMe ? 'Shared with me' : 'Shared link'}>
            <PeopleIcon sx={{ fontSize: 16, color: item.sharedWithMe ? 'primary.main' : 'text.secondary' }} />
          </Tooltip>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ width: 140, textAlign: 'center', flexShrink: 0 }}>
        {new Date(item.modifiedTime).toLocaleDateString()}
      </Typography>
    </Stack>
  );
}

/* ================= Main Dialog ================= */

export default function GoogleListItemDialog({ open, onCancel, dataConnection, onConfirm, loading }) {
  const [breadcrumb, setBreadcrumb] = useState([ROOT]);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const currentFolderId = breadcrumb[breadcrumb.length - 1].id;

  const {
    data: rootItems = [],
    isFetching: isFetchingFolder,
    isError: isRootError,
    error: rootError
  } = useGoogleRootFolders("spreadsheet");

  const {
    data: folderItems = [],
    isFetching: isFetchingItemFolder,
    isError: isFolderError,
    error: folderError
  } = useGoogleFolderItems(currentFolderId !== 'root' ? currentFolderId : null, "spreadsheet");

  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const error = rootError || folderError;
    if (!error) return;

    setErrorMessage(error?.raw?.message || 'You do not have permission to access this Google Drive');
    setOpenError(true);
  }, [rootError, folderError]);

  const handleClose = () => setOpenError(false);

  // 🔍 FILTER SEARCH & TABS
  const rawItems = currentFolderId === 'root' ? rootItems : folderItems;

  const filteredItems = useMemo(() => {
    if (currentFolderId === 'root') {
      if (activeTab === 'shared-with-me') {
        return rawItems.filter((item) => item.sharedWithMe === true);
      } else if (activeTab === 'my-drive') {
        return rawItems.filter((item) => !item.sharedWithMe);
      } else {
        return rawItems;
      }
    }
    return rawItems;
  }, [rawItems, currentFolderId, activeTab]);

  const itemsToRender = useMemo(() => {
    return filteredItems.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [filteredItems, search]);

  const handleOpenFolder = (folder) => {
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setSearch('');
  };

  const handleSelectItem = (item) => {
    onConfirm(item);
  };

  const handleBack = () => {
    if (breadcrumb.length <= 1) return;
    setBreadcrumb((prev) => prev.slice(0, -1));
    setSearch('');
  };

  const handleBreadcrumbNavigate = (index) => {
    setBreadcrumb((prev) => prev.slice(0, index + 1));
    setSearch('');
  };

  const isPageLoading = isFetchingFolder || isFetchingItemFolder;

  const sidebarItems = [
    { id: 'all', label: 'All Files', icon: <StorageIcon /> },
    { id: 'my-drive', label: 'My Drive', icon: <CloudQueueIcon /> },
    { id: 'shared-with-me', label: 'Shared with me', icon: <PeopleIcon /> }
  ];

  return (
    <>
      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.modal + 1 })} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar open={openError} onClose={handleClose} autoHideDuration={5000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <SnackbarContent
          sx={{
            backgroundColor: '#fff',
            minWidth: 360,
            p: 2,
            boxShadow: 3,
            borderRadius: 1,
            borderLeft: '4px solid #d32f2f'
          }}
          message={
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <ErrorOutlineIcon sx={{ mr: 2, fontSize: 28, color: 'error.main' }} />

              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold" color="error.main">
                  Google Drive Error
                </Typography>
                <Typography variant="subtitle2" color="text.primary">
                  {errorMessage || 'Failed to load Google Drive data'}
                </Typography>
              </Box>

              <IconButton size="small" onClick={handleClose} sx={{ ml: 2 }}>
                <CloseIcon />
              </IconButton>
            </Box>
          }
        />
      </Snackbar>

      <Dialog fullScreen open={open} onClose={onCancel} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1, fontWeight: 'bold' }} variant="h5">
              Google Drive
            </Typography>
            <Button color="inherit" onClick={onCancel}>
              CLOSE
            </Button>
          </Toolbar>
        </AppBar>

        {/* Outer Layout containing Sidebar & Main Content */}
        <Stack direction="row" sx={{ height: 'calc(100vh - 64px)', bgcolor: 'background.paper' }}>
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: 80, sm: 240 },
              borderRight: '1px solid',
              borderColor: 'divider',
              py: 3,
              px: { xs: 1, sm: 2 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {sidebarItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <Button
                  key={item.id}
                  variant={isActive ? 'contained' : 'text'}
                  color={isActive ? 'primary' : 'inherit'}
                  onClick={() => {
                    setActiveTab(item.id);
                    setBreadcrumb([ROOT]);
                    setSearch('');
                  }}
                  startIcon={item.icon}
                  sx={{
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    px: { xs: 1, sm: 2 },
                    py: 1.2,
                    borderRadius: 50, // Pill shaped active buttons, exactly like Google Drive
                    textTransform: 'none',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: 14,
                    color: isActive ? '#fff' : 'text.primary',
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0, sm: 1.5 },
                      marginLeft: 0
                    },
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{item.label}</Box>
                </Button>
              ); 
            })}
          </Box>

          {/* Main Content Area */}
          <Stack sx={{ flex: 1, px: { xs: 2, sm: 4 }, py: 3, overflow: 'hidden' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Tooltip title="Back">
                <span>
                  <IconButton size="small" disabled={breadcrumb.length <= 1} onClick={handleBack}>
                    <ArrowBackIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <DriveBreadcrumb breadcrumb={breadcrumb} onNavigate={handleBreadcrumbNavigate} activeTab={activeTab} />
            </Stack>

            {/* 🔍 SEARCH */}
            <TextField
              size="small"
              placeholder="Search file or folder..."
              value={search}
              fullWidth
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Column header */}
            <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.5 }}>
              <Typography sx={{ flex: 1 }} fontWeight={500}>
                Name
              </Typography>
              <Typography sx={{ width: 140, textAlign: 'center' }} fontWeight={500}>
                Modified
              </Typography>
            </Stack>

            <Divider />

            {isPageLoading && <LinearProgress />}

            {/* List */}
            <Stack sx={{ flex: 1, overflowY: 'auto', mt: 1, pr: 1 }}>
              {itemsToRender.map((item) => (
                <DriveRow key={item.id} item={item} onSelect={handleSelectItem} onOpen={handleOpenFolder} />
              ))}

              {itemsToRender.length === 0 && (
                <Typography sx={{ textAlign: 'center', mt: 4 }} color="text.secondary">
                  No results found
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}